import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Modal, Button, Input, InputNumber, Form, Space, Select, Flex, Tooltip, Divider, Tag, notification } from 'antd';
import { UndoOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import { v4 as uuid } from 'uuid';

import FormHeader from '@controls/form-header/form-header';
import Toolbar from '@controls/toolbar/toolbar';

import { exception, securityRestriction } from '@extensions/notification';

import { userLoaded } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';

import { serverFetch } from '@flp/server';

import { ICity } from '@entities/city';
import { ICountry } from '@entities/country';
import { IUserSession } from '@entities/user-session';
import { IConsignee } from '@entities/consignee';

import { Permission, hasPermission } from '@enums/permission';
import { Currency, enumSign as currencySign } from '@enums/currency';

import { ITariff } from '@flp/entities/tariff';
import { ITariffLine } from '@flp/entities/tariff-line';
import { IPackageTemplate } from '@flp/entities/package-template';

import { PaymentType, enumLabel as paymentTypeLabel } from '@flp/enums/payment-type';

const Tariff = () => {
    const { TextArea } = Input;
    const [form] = Form.useForm();

    const { headerId, id, isCopy } = useParams();

    const navigate = useNavigate();
    const [api, contextHolder] = notification.useNotification();
    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [entity, setEntity] = useState<ITariff>({
        id: undefined,
        headerId: headerId,
        consigneeId: undefined,
        cityId: undefined,
        lines: [],
    });

    const [consignees, setConsignees] = useState<Array<IConsignee>>([]);
    const [cities, setCities] = useState<Array<ICity>>([]);

    const [loading, setLoading] = useState<boolean>(false);
    const [canManage] = useState<boolean>(hasPermission(userSession.permissions, Permission.ManageTradingPlatforms));

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch('countries', { method: 'GET', queryParams: { withTransportOnly: true } })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('packagetemplates', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения упаковок', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('consignees', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения грузополучателей', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('cities', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            if (id) {
                promises.push(
                    await serverFetch(`tariffs/${id}`, { method: 'GET' })
                        .then((data) => {
                            return data;
                        })
                        .catch((ex) => {
                            exception(api, 'Ошибка получения тарифа', ex, () => d(userLoaded(undefined)));
                        }),
                );
            }

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                const countries = result[0][0] as Array<ICountry>;
                const packages = result[0][1] as Array<IPackageTemplate>;
                const packagesPerItem = packages.filter((p) => p.paymentType == PaymentType.Item);

                setConsignees(result[0][2]);
                setCities(result[0][3]);

                let tmpTariff: ITariff;

                let lines: Array<ITariffLine> = [];

                if (id) {
                    tmpTariff = result[0][4] as ITariff;

                    if (isCopy) {
                        tmpTariff.id = undefined;
                        tmpTariff.consigneeId = undefined;
                        tmpTariff.cityId = undefined;
                    }

                    lines = tmpTariff.lines.filter((l) => {
                        const country = countries.find((c) => c.id === l.countryId);
                        if (country) {
                            l.countryName = country.name;
                            return l;
                        }
                    });

                    lines.map((l) => {
                        if (isCopy) {
                            l.id = undefined;

                            l.details.map((d) => {
                                d.id = undefined;
                            });
                        }
                    });
                } else {
                    tmpTariff = entity;

                    countries.map((c) => {
                        const line: ITariffLine = {
                            id: undefined,
                            tariffId: undefined,
                            countryId: c.id,
                            countryName: c.name,
                            details: [],
                        };

                        lines.push(line);
                    });
                }

                lines.map((l) => {
                    const palletDetail = l.details.find((d) => d.paymentType == PaymentType.Pallet);
                    if (!palletDetail) {
                        l.details.push({
                            id: undefined,
                            tariffLineId: l.id,
                            truckPrice: undefined,
                            truckPriceUsd: undefined,
                            packageTemplateName: paymentTypeLabel(PaymentType.Pallet),
                            paymentType: PaymentType.Pallet,
                        });
                    } else {
                        palletDetail.packageTemplateName = paymentTypeLabel(PaymentType.Pallet);
                    }

                    packagesPerItem.map((p) => {
                        const itemDetail = l.details.find((d) => d.packageTemplateId == p.id);
                        if (!itemDetail) {
                            packagesPerItem.map((p) => {
                                l.details.push({
                                    id: undefined,
                                    tariffLineId: l.id,
                                    truckPrice: undefined,
                                    truckPriceUsd: undefined,
                                    packageTemplateId: p.id,
                                    packageTemplateName: p.name,
                                    paymentType: PaymentType.Item,
                                    useUsd: p.useUsd,
                                });
                            });
                        } else {
                            itemDetail.useUsd = p.useUsd;
                        }
                    });
                });

                setEntity({ ...tmpTariff, lines: lines });

                setLoading(false);
            });
        };

        fetchData();
        return () => {
            cleanup = true;
        };
    }, []);

    const resetDefault = () => {
        setLoading(true);

        serverFetch(`tariffs/${id}/default`, { method: 'GET' })
            .then((result: Array<ITariffLine>) => {
                setLoading(false);

                const lines = [...entity.lines];

                for (let i = 0; i < result.length; i++) {
                    const defaultLine = result[i];

                    const line = lines.find((l) => l.countryId === defaultLine.countryId);
                    if (line) {
                        line.details.map((d) => {
                            const defaultDetail = defaultLine?.details.find((e) => e.id == d.id);
                            d.truckPrice = defaultDetail?.truckPrice;
                            d.truckPriceUsd = defaultDetail?.truckPriceUsd;

                            form.setFieldValue(`truckPrice${line?.id}${line.countryId}${d.packageTemplateId}`, defaultDetail?.truckPrice);
                            form.setFieldValue(
                                `truckPriceUsd${line?.id}}${line.countryId}${d.packageTemplateId}`,
                                defaultDetail?.truckPriceUsd,
                            );
                        });
                    }
                }

                setEntity({ ...entity, lines: lines });
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка получения тарифа по умолчанию', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSave = () => {
        setLoading(true);

        const data = { ...entity };

        data.lines.map((l) => {
            l.details.map((d) => {
                d.truckPrice = form.getFieldValue(`truckPrice${l.id}${l.countryId}${d.packageTemplateId}`);
                d.truckPriceUsd = form.getFieldValue(`truckPriceUsd${l.id}${l.countryId}${d.packageTemplateId}`);
            });
        });

        serverFetch(`tariffs`, { method: id && !isCopy ? 'PUT' : 'POST', bodyData: data })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка сохранения тарифа', ex, () => d(userLoaded(undefined)));
            });
    };

    const onConsigneeChanged = (id: string) => {
        const consignee = consignees.find((m) => m.id === id);

        const city = cities.find((c) => c.id === consignee?.cityId);

        const data = { ...entity };
        data.consigneeId = id;
        data.cityId = city?.id;

        data.lines = data.lines.map((l) => {
            l.details.map((d) => {
                form.setFieldValue(`truckPriceUsd${l.id}${l.countryId}${d.packageTemplateId}`, d.truckPriceUsd);
            });

            return l;
        });

        onCityChanged(data);
    };

    const onCityChanged = (data: ITariff) => {
        form.setFieldValue('cityId', data.cityId);
    };

    const renderLines = () => {
        return entity.lines.map((l) => {
            return (
                <div key={uuid()}>
                    <Form.Item>
                        <Divider titlePlacement='left' style={{ marginBottom: 0 }}>
                            {l.countryName}
                        </Divider>
                    </Form.Item>
                    <Flex wrap gap='large'>
                        {l.details.map((d) => {
                            return (
                                <Flex key={uuid()}>
                                    <Form.Item label={d.packageTemplateName} style={{ marginBottom: 0 }}>
                                        <Space.Compact>
                                            <Form.Item
                                                noStyle
                                                initialValue={d.truckPrice}
                                                name={`truckPrice${l.id}${l.countryId}${d.packageTemplateId}`}
                                            >
                                                <InputNumber decimalSeparator=',' precision={2} min={0} disabled={!canManage} />
                                            </Form.Item>
                                            <Space.Addon>
                                                <Tooltip title='Транспортная доставка' placement='topRight'>
                                                    <div>{currencySign(Currency.Eur)}</div>
                                                </Tooltip>
                                            </Space.Addon>
                                        </Space.Compact>
                                    </Form.Item>
                                    {d.useUsd && (
                                        <Form.Item style={{ marginBottom: 0, marginLeft: 5 }}>
                                            <Space.Compact>
                                                <Form.Item
                                                    noStyle
                                                    initialValue={d.truckPriceUsd}
                                                    name={`truckPriceUsd${l.id}${l.countryId}${d.packageTemplateId}`}
                                                >
                                                    <InputNumber decimalSeparator=',' precision={2} min={0} />
                                                </Form.Item>
                                                <Space.Addon>
                                                    <Tooltip title='Транспортная доставка' placement='topRight'>
                                                        <div>{currencySign(Currency.Usd)}</div>
                                                    </Tooltip>
                                                </Space.Addon>
                                            </Space.Compact>
                                        </Form.Item>
                                    )}
                                </Flex>
                            );
                        })}
                    </Flex>
                </div>
            );
        });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                style={{ padding: '10px 0 0' }}
                commands={[
                    {
                        type: 'primary',
                        label: 'Сбросить тариф по умолчанию',
                        key: 'resetDefault',
                        icon: <UndoOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteTransportTariff)) {
                                securityRestriction(api, [Permission.DeleteTransportTariff]);
                                return;
                            }

                            modal.confirm({
                                title: `Сбросить значения тарифа по умолчанию?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    resetDefault();
                                },
                            });
                        },
                    },
                ]}
            />
        );
    };

    return (
        <>
            <FormHeader title={`${id && !isCopy ? (!canManage ? 'Информация о тарифе' : 'Изменить тариф') : 'Добавить тариф'}`} />
            {!loading && (
                <Form colon={false} labelCol={{ span: 3 }} wrapperCol={{ span: 9 }} onFinish={onSave} form={form}>
                    <Form.Item
                        initialValue={entity.consigneeId}
                        required
                        label='Клиент'
                        name='consigneeId'
                        rules={[{ required: true, message: 'Выберите клиента' }]}
                    >
                        <Select
                            allowClear
                            disabled={!canManage}
                            autoFocus
                            onChange={(value) => onConsigneeChanged(value)}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                            }}
                            options={consignees.map((с) => {
                                let label = с.keyName
                                    ? `${с.code} / ${с.keyName} / ${с.fullName} / ${с.cityName}`
                                    : `${с.code} / ${с.fullName} / ${с.cityName}`;
                                return { value: с.id, label: label };
                            })}
                        />
                    </Form.Item>
                    <Form.Item
                        initialValue={entity.cityId}
                        required
                        label='Расчетный город'
                        name='cityId'
                        rules={[{ required: true, message: 'Выберите город' }]}
                    >
                        <Select
                            disabled={!canManage}
                            onChange={(value) => {
                                const data = { ...entity };
                                data.cityId = value;

                                onCityChanged(data);
                            }}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={cities.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        ></Select>
                    </Form.Item>

                    <Form.Item label='Комментарий' name='comment' initialValue={entity?.comment}>
                        <TextArea
                            disabled={!canManage}
                            rows={4}
                            onChange={(data) => {
                                setEntity({ ...entity, comment: data.target.value });
                            }}
                        />
                    </Form.Item>

                    <Form.Item wrapperCol={{ offset: 1, span: 11 }} style={{ marginBottom: 0 }}>
                        {renderToolbar()}
                    </Form.Item>

                    <Form.Item wrapperCol={{ offset: 1, span: 11 }}>{entity.lines && renderLines()}</Form.Item>

                    <Form.Item label='' wrapperCol={{ offset: 3, span: 9 }}>
                        <Space size={'small'} style={{ float: 'right' }}>
                            <Button type='text' onClick={() => navigate(-1)}>
                                Отменить
                            </Button>
                            {canManage && (
                                <Button type='primary' htmlType='submit' loading={loading}>
                                    Сохранить
                                </Button>
                            )}
                        </Space>
                    </Form.Item>
                </Form>
            )}

            {contextHolder}
            {modalContextHolder}
        </>
    );
};

export default Tariff;
